import React, { useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { QuestionCard } from '../components/QuestionCard';
import { SortableQuestionCard } from '../components/SortableQuestionCard';
import { AddQuestionButton } from '../components/AddQuestionButton';
import { QuestionListSkeleton, QuestionSkeleton } from '../components/QuestionSkeleton';
import { useQuestions } from '../hooks/useQuestions';
import { useQuestionReorder } from '../hooks/useQuestionReorder';
import { useProgressiveLoading } from '../hooks/useProgressiveLoading';
import { CreateQuestionDTO, UpdateQuestionDTO } from '../dtos';
import { QuestionDTO } from '../dtos';
import { surveyApi } from '../../survey/api/surveyApi';
import { useQuery } from '@tanstack/react-query';
import { usePostHog } from '../../../hooks/usePostHog';
import { NAVIGATION_EVENTS } from '../../../utils/trackingEvents';
import { SurveyLockedBanner, SurveyDraftBanner } from '../../survey/components';
import { useSetToDraft, usePublishSurvey } from '../../survey/hooks/useSurveys';
import { surveyQueryKeys } from '../../survey/hooks/queryKeys';
import { useSurveyBreadcrumb } from '../../survey/hooks/useSurveyBreadcrumb';
import { useSetPageActions } from '../../../contexts/PageActionsContext';

interface SurveyBuilderParams extends Record<string, string | undefined> {
  surveyId: string;
  templateId?: string;
}

export const SurveyBuilder: React.FC = () => {
  const { surveyId, templateId } = useParams<SurveyBuilderParams>();
  const { captureEvent } = usePostHog();
  const { setHelp } = useSetPageActions();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Set breadcrumb metadata for survey title
  useSurveyBreadcrumb(surveyId);

  // Register help in the breadcrumb bar
  useEffect(() => {
    setHelp({
      title: 'Survey Builder Guide',
      sections: [
        {
          title: 'Adding Questions',
          content:
            'Use the + button in the bottom right corner to add new questions to your survey.',
        },
        {
          title: 'Template Questions',
          content:
            'Questions marked with a lock icon are from templates and cannot be edited or reordered.',
        },
        ...(isMobile
          ? []
          : [
              {
                title: 'Reordering Questions',
                content: 'Use the drag handle on custom questions to reorder them by dragging.',
              },
            ]),
        {
          title: 'Editing Questions',
          content: 'Click the edit icon on any custom question to modify its settings and content.',
        },
      ],
    });
  }, [setHelp, isMobile]);

  // Track survey builder opened when component mounts
  useEffect(() => {
    if (surveyId) {
      captureEvent(NAVIGATION_EVENTS.SURVEY_BUILDER_OPENED, {
        survey_id: surveyId,
        template_id: templateId || null,
        is_template: !!templateId,
      });
    }
  }, [surveyId, templateId, captureEvent]);

  // Fetch survey details to get domainId
  const {
    data: surveyData,
    isLoading: surveyLoading,
    isFetching: surveyFetching,
    error: surveyError,
  } = useQuery({
    queryKey: surveyQueryKeys.survey(surveyId!),
    queryFn: () => surveyApi.getSurvey(surveyId!),
    enabled: !!surveyId,
  });

  // Hook for setting survey to draft
  const setToDraftMutation = useSetToDraft(surveyData?.data?.subjectId || '');

  // Hook for publishing survey
  const publishSurveyMutation = usePublishSurvey(surveyData?.data?.subjectId || '');

  // Check if survey is locked (ACTIVE status)
  const isSurveyLocked = surveyData?.data?.status === 'ACTIVE';

  // Check if survey is in draft status
  const isSurveyDraft = surveyData?.data?.status === 'DRAFT';

  // Handler for setting survey to draft
  const handleSetToDraft = useCallback(async () => {
    if (!surveyId) return;

    try {
      await setToDraftMutation.mutateAsync(surveyId);
    } catch (error) {
      console.error('Failed to set survey to draft:', error);
    }
  }, [surveyId, setToDraftMutation]);

  // Handler for publishing survey
  const handlePublishSurvey = useCallback(async () => {
    if (!surveyId) return;

    try {
      await publishSurveyMutation.mutateAsync(surveyId);
    } catch (error) {
      console.error('Failed to publish survey:', error);
    }
  }, [surveyId, publishSurveyMutation]);

  // Fetch questions data
  const {
    data: questions = [],
    isLoading,
    error,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    isUpdating,
  } = useQuestions({
    surveyId: surveyId || '',
    templateId,
  });

  // Separate template and custom questions
  const { templateQuestions, customQuestions } = useMemo(() => {
    const sorted = [...questions].sort((a, b) => a.order - b.order);
    const template = sorted.filter((q) => q.isFromTemplate === true);
    const custom = sorted.filter((q) => q.isFromTemplate !== true);
    return { templateQuestions: template, customQuestions: custom };
  }, [questions]);

  // Drag & drop reordering (only for custom questions)
  const {
    onDragEnd,
    isReordering,
    sortedQuestions: sortedCustomQuestions,
  } = useQuestionReorder({
    surveyId: surveyId || '',
    questions: customQuestions,
  });

  // Progressive loading for large question lists (only for custom questions)
  const {
    visibleItems: visibleCustomQuestions,
    hasMore,
    isLoadingMore,
    loadedCount: loadedCustomCount,
    totalCount: totalCustomCount,
    loadMore,
    getSkeletonCount,
    progressPercentage,
  } = useProgressiveLoading({
    items: sortedCustomQuestions,
    initialBatchSize: 10,
    incrementSize: 5,
    autoLoadThreshold: 3,
    enabled: sortedCustomQuestions.length > 15, // Only use progressive loading for large lists
  });

  // Total counts for display
  const totalQuestions = templateQuestions.length + sortedCustomQuestions.length;
  // visibleTotalQuestions can be derived from: templateQuestions.length + (totalCustomCount > 15 ? loadedCustomCount : sortedCustomQuestions.length)

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCreateQuestion = useCallback(
    async (data: CreateQuestionDTO) => {
      const survey = surveyData?.data;
      if (!survey) {
        throw new Error('Survey data not available');
      }

      const enrichedData: CreateQuestionDTO = {
        ...data,
        tenantId: survey.tenantId,
        subjectId: survey.subjectId,
      };

      await createQuestion.mutateAsync(enrichedData);
    },
    [createQuestion, surveyData]
  );

  const handleUpdateQuestion = useCallback(
    async (questionId: string, data: UpdateQuestionDTO) => {
      await updateQuestion.mutateAsync({ questionId, data });
    },
    [updateQuestion]
  );

  const handleDeleteQuestion = useCallback(
    async (questionId: string) => {
      await deleteQuestion.mutateAsync(questionId);
    },
    [deleteQuestion]
  );

  // Combined loading and error states
  const isLoadingData =
    surveyLoading ||
    surveyFetching ||
    isLoading ||
    setToDraftMutation.isPending ||
    publishSurveyMutation.isPending;
  const hasError = surveyError || error;

  const renderLoadingSkeleton = () => (
    <QuestionListSkeleton count={5} variant="card" showDragHandle={true} staggered={true} />
  );

  const renderEmptyState = () => {
    const hasTemplateQuestions = templateQuestions.length > 0;
    const hasCustomQuestions = customQuestions.length > 0;

    if (!hasTemplateQuestions && !hasCustomQuestions) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'action.hover',
            border: '2px dashed',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Questions Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the <strong>+ button</strong> in the bottom right corner to add your first question
          </Typography>
        </Paper>
      );
    }

    if (hasTemplateQuestions && !hasCustomQuestions) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'action.hover',
            border: '2px dashed',
            borderColor: 'divider',
            mt: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Custom Questions Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            You have {templateQuestions.length} template question
            {templateQuestions.length !== 1 ? 's' : ''}. Add custom questions to personalize your
            survey.
          </Typography>
          <Typography variant="body2" color="primary" fontWeight={600}>
            Click the + button to add questions
          </Typography>
        </Paper>
      );
    }

    return null;
  };

  const renderErrorState = () => (
    <Alert severity="error" sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Failed to load data
      </Typography>
      <Typography variant="body2">
        {surveyError?.message ||
          error?.message ||
          'An unexpected error occurred. Please try refreshing the page.'}
      </Typography>
    </Alert>
  );

  // Template Questions Section Component
  const TemplateQuestionsSection: React.FC = () => {
    if (templateQuestions.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            Template Questions
          </Typography>
          <Chip
            icon={<LockIcon />}
            label={templateQuestions.length}
            size="small"
            color="secondary"
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary">
            These questions cannot be edited or reordered. Click the chevron to preview.
          </Typography>
        </Box>

        <Box sx={{ pl: 2 }}>
          <Stack spacing={3}>
            {templateQuestions.map((question: QuestionDTO, index: number) => (
              <Box
                key={question.id}
                sx={{
                  position: 'relative',
                }}
              >
                <QuestionCard
                  question={question}
                  index={index}
                  onUpdate={handleUpdateQuestion}
                  onDelete={handleDeleteQuestion}
                  isUpdating={false}
                  isDragDisabled={true}
                  isEditDisabled={true}
                  isDeleteDisabled={true}
                  accessibilityProps={{
                    'aria-label': `Template question ${index + 1}: ${question.title.en || 'Untitled'}. This question cannot be edited.`,
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    );
  };

  // Custom Questions Section Component
  const CustomQuestionsSection: React.FC = () => {
    if (customQuestions.length === 0) return null;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Typography variant="h6" color="text.secondary">
            Questions
          </Typography>
          <Chip
            label={sortedCustomQuestions.length}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Box sx={{ overflow: 'visible', pl: 2 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={onDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={visibleCustomQuestions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <Box
                sx={{
                  borderRadius: 1,
                  transition: 'background-color 0.2s ease',
                  minHeight: '50px',
                  overflow: 'visible',
                }}
              >
                <Stack spacing={3}>
                  {/* Progress indicator for large lists */}
                  {totalCustomCount > 15 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {loadedCustomCount} of {totalCustomCount} custom questions
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: 4,
                          backgroundColor: 'action.hover',
                          borderRadius: 2,
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${progressPercentage}%`,
                            backgroundColor: 'primary.main',
                            borderRadius: 2,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Custom questions list */}
                  {visibleCustomQuestions.map((question: QuestionDTO, index: number) => (
                    <SortableQuestionCard
                      key={question.id}
                      question={question}
                      index={templateQuestions.length + index} // Adjust index to account for template questions
                      onUpdate={handleUpdateQuestion}
                      onDelete={handleDeleteQuestion}
                      isUpdating={isUpdating || isReordering}
                      isDragDisabled={isUpdating || isReordering || isSurveyLocked}
                      isEditDisabled={isSurveyLocked}
                      isDeleteDisabled={isSurveyLocked}
                    />
                  ))}

                  {/* Loading more skeletons */}
                  {isLoadingMore && (
                    <Stack spacing={3}>
                      {Array.from({ length: getSkeletonCount() }, (_, index) => (
                        <QuestionSkeleton
                          key={`loading-${index}`}
                          variant="card"
                          showDragHandle={true}
                          animate={true}
                        />
                      ))}
                    </Stack>
                  )}

                  {/* Load more button */}
                  {hasMore && !isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={loadMore}
                        disabled={isUpdating || isReordering}
                      >
                        Load More Questions ({totalCustomCount - loadedCustomCount} remaining)
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            </SortableContext>
          </DndContext>
        </Box>
      </Box>
    );
  };

  const renderQuestionsList = () => {
    const hasAnyQuestions = totalQuestions > 0;
    const hasTemplateQuestions = templateQuestions.length > 0;
    const hasCustomQuestions = customQuestions.length > 0;

    if (!hasAnyQuestions) {
      return renderEmptyState();
    }

    return (
      <Stack spacing={4}>
        {/* Template Questions Section */}
        <TemplateQuestionsSection />

        {/* Custom Questions Section */}
        <CustomQuestionsSection />

        {/* Empty state for custom questions when only template questions exist */}
        {hasTemplateQuestions && !hasCustomQuestions && renderEmptyState()}
      </Stack>
    );
  };

  if (!surveyId) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">Survey ID is required to build questions</Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Main Content */}
      <Container maxWidth={false} disableGutters sx={{ overflow: 'visible' }}>
        {/* Banner Spacer Zone — fixed height keeps content aligned regardless of banner visibility */}
        <Box sx={{ minHeight: 72 }}>
          {!isLoadingData && isSurveyLocked && (
            <SurveyLockedBanner
              onSetToDraft={handleSetToDraft}
              isLoading={setToDraftMutation.isPending}
            />
          )}

          {!isLoadingData && isSurveyDraft && (
            <SurveyDraftBanner
              onPublish={handlePublishSurvey}
              isLoading={publishSurveyMutation.isPending}
            />
          )}
        </Box>

        {hasError && renderErrorState()}

        {isLoadingData ? renderLoadingSkeleton() : renderQuestionsList()}

        {/* Floating Action Button */}
        {surveyData?.data && (
          <AddQuestionButton
            surveyId={surveyId || ''}
            templateId={templateId}
            onAdd={handleCreateQuestion}
            disabled={isUpdating || isReordering || isSurveyLocked}
            size={isMobile ? 'medium' : 'large'}
            variant="floating"
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 62 },
              right: { xs: 16, sm: 62 },
              zIndex: 1000,
            }}
          />
        )}
      </Container>
    </>
  );
};
